// Every display string for the folio editor, kept out of the component for the same reason the Getting
// Started copy is: a wording change should be a one-line edit that touches nothing else.

export const FOLIO_EDITOR_STRINGS = {
    pageTitle: 'Extension Folios',

    introOwned: 'Folios your organization has authored. These are visible and installable only to you.',
    introGlobal: 'Every folio in the catalog, including the global connectors every organization installs from.',

    notEntitled: 'Authoring extension folios is not enabled for this organization. Talk to your account team if you need it.',

    loading: 'Loading folios...',
    loadFailed: 'We could not load the folio catalog. Please try again.',
    empty: 'No folios yet. Create one to bundle an integration’s capabilities behind one set of common settings.',

    newFolio: 'New folio',
    edit: 'Edit',
    copy: 'Copy',
    copyTooltip: 'Start a new folio from this one, to develop against without touching the original.',
    copyName: (name: string) => `${name} (copy)`,
    delete: 'Delete',
    save: 'Save',
    saving: 'Saving...',
    cancel: 'Cancel',

    namePlaceholder: 'Name this connector',
    newTitle: 'New folio',

    idLabel: 'Folio id',
    /** The server requires an author's new ids to carry this prefix, so the field says so up front. */
    idNewDescription: (prefix: string) => prefix
        ? `Must start with "${prefix}". This is the id installed connections reference, and it cannot be changed later.`
        : 'This is the id installed connections reference, and it cannot be changed later.',

    /** Summary line under a folio's name in the list. */
    folioSummary: (id: string, subCount: number, owner?: string) => {
        const capabilities = `${subCount} ${subCount === 1 ? 'capability' : 'capabilities'}`;
        return owner ? `${id} · ${capabilities} · owned by ${owner}` : `${id} · ${capabilities} · global`;
    },

    deleteTitle: 'Delete this folio?',
    deleteConfirm: (name: string) =>
        `${name} will be removed from the catalog. Organizations that already installed it keep their credentials, so it cannot be deleted while any installation remains.`,
    deleteFailed: 'We could not delete that folio.',

    // Editing modes.
    rawHelp: 'The whole definition, for fields the form does not cover or for pasting one wholesale. Edits here and in the form are the same document, so switching between them loses nothing.',
    documentLabel: 'Folio definition (JSON)',
    invalidJson: (message: string) => `That is not valid JSON: ${message}`,
    blockedHint: 'Fix the errors above to save.',
    idRequired: 'A folio id is required.',
    saveFailed: 'We could not save that folio.',

    // Form tabs. A folio is several unrelated subjects; each gets one.
    tabBasics: 'Basics',
    tabCapabilities: 'Capabilities',
    tabShared: 'Common settings',
    tabEndpoints: 'Endpoints',
    tabDocumentation: 'Documentation',
    tabAudience: 'Audience',
    showRaw: 'Show raw JSON',

    // Help lines, one per tab.
    sectionEndpointsHelp: 'Shared by every capability in this folio. The ordering endpoints only matter if one of them places orders.',
    // tabShared is named for what the block holds, not for the commonest thing in it: shared credentials
    // live there, but so do endpoints, account numbers, catalog choices and anything else every capability
    // reads. It also matches the heading the install screen puts on the same fields, so an author and an
    // operator are talking about the same box.
    sectionSharedHelp: 'Settings every capability in this folio reads, credentials among them. A capability can declare a field with the same name to override this one for itself, which is how one folio holds both an FTP account and an API key.',
    sectionHelpLinksHelp: 'Links that apply to the whole connector. Documentation for a single capability belongs on the capability instead.',
    sectionCapabilitiesHelp: 'What this connector can do. Each one becomes an installable capability, using the common settings above plus anything it declares for itself.',
    sectionAudienceHelp: 'Who can install this folio. Only a global folio admin can set these.',

    // Basics.
    folioDescription: 'Description',
    folioVendor: 'Default vendor',
    folioVendorHelp: 'The vendor name products and orders are attributed to.',
    folioBaseUrl: 'Base URL',
    folioBaseUrlHelp: 'Optional. Every endpoint below and on each capability is called at this host: a full URL keeps its path and takes this host, a relative one is hung off it. Leave empty to call each endpoint exactly as written.',
    folioEncryption: 'Encryption URL',
    folioValidate: 'Validate URL',
    folioValidateHelp: 'Without one, credentials cannot be checked and a connection can never be proven working.',
    showOrderUrls: 'Show ordering endpoints',
    hideOrderUrls: 'Hide ordering endpoints',

    // Audience.
    folioOrganizations: 'Organizations',
    folioOrganizationsHelp: 'Comma separated. Leave empty for every organization. Exactly one makes that organization the owner.',
    folioEntitlements: 'Required entitlements',
    folioEntitlementsHelp: 'An organization needs at least one of these to be offered the folio. The list is every tag already used as a gate on an extension, a folio, or this organization.',
    folioEntitlementsPlaceholder: 'No entitlement required',
    folioEntitlementsLoading: 'Loading entitlements…',
    folioEntitlementAdd: 'Add an entitlement not listed',
    folioEntitlementAddPlaceholder: 'e.g. griffinit',
    add: 'Add',
    folioProductLevel: 'Product levels',
    folioProductLevelHelp: 'Which levels are offered the folio.',
    folioProductLevelPlaceholder: 'All product levels',

    // Capabilities.
    addCapability: 'Add capability',
    removeCapability: 'Remove capability',
    untitledCapability: 'Untitled capability',
    onByDefault: 'On by default',
    capabilityIdentifier: 'Identifier',
    capabilityIdentifierHelp: 'Stored credentials are keyed by this, so renaming it on a published folio orphans them.',
    capabilityName: 'Name',
    capabilityDescription: 'Description',
    capabilityCategory: 'Category',
    capabilitySubCategory: 'Sub category',
    capabilitySubCategoryHelp: 'Only for capabilities driven by something other than their own URL, such as import_configurator.',
    capabilityUrl: 'URL',
    capabilityUrlHelp: 'Where this capability posts. Leave empty only when a sub category says what runs it instead.',
    capabilityFields: 'Capability-specific settings',
    capabilityFieldsHelp: 'Fields only this capability needs — its own credentials, but also anything else that is true of this capability alone. A name that matches a common field overrides it here.',
    capabilityHelpLinks: 'Documentation links',
    capabilityValueMaps: 'Value translations',
    capabilityValueMapsHelp: 'Values the import file uses that the importer should read as something else, such as a distributor’s own in-stock codes. They apply over whatever the extension sends for the same value, so a code can be corrected here without redeploying the extension.',
    valueMapField: 'Field',
    valueMapFrom: 'Value in the file',
    valueMapTo: 'Read as',
    addValueMap: 'Add translation',
    removeValueMap: 'Remove translation',
    capabilityHelpLinksHelp: 'Shown beside the fields for this capability: where to request access, which account to use, whatever else an operator has to go and read. This is where the FTP-versus-API distinction gets explained.',

    // Credential fields.
    untitledField: 'New field',
    fieldShadowsBadge: 'overrides a common field',
    addField: 'Add field',
    removeField: 'Remove field',
    noFields: 'No fields. This capability asks the operator for nothing.',
    fieldName: 'Field name',
    fieldLabel: 'Label shown to the operator',
    fieldToolTip: 'Help text',
    fieldToolTipHelp: 'Shown as an info icon beside the label. Markdown works, including links — this is where to say which screen of the vendor portal the value comes from.',
    fieldHasToolTip: 'has help',
    fieldType: 'Type',
    fieldDataType: 'Value',
    fieldDefault: 'Default',
    fieldProtected: 'Protected (a secret)',
    fieldOptional: 'Optional',
    fieldShadowsShared: (name: string) => `Overrides the shared "${name}" for this capability only.`,

    // PSA quote link.
    tabQuoteLink: 'Quote link',
    sectionQuoteLinkHelp: 'A button the operator adds to their PSA’s quote screen. Clicking it opens Adaptive Catalog on that quote and runs a direct-import capability, landing on a prepared cart. The setup screen shows the description and steps below beside the generated link.',
    quoteLinkEnabled: 'This folio offers a PSA quote link',
    quoteLinkCapability: 'Capability the link runs',
    quoteLinkCapabilityPlaceholder: 'Pick a direct-import capability',
    quoteLinkMissingCapability: (identifier: string) => `${identifier} (not a capability of this folio)`,
    quoteLinkParameter: 'Query string',
    quoteLinkParameterHelp: 'Appended to the link, with the PSA’s substitution tags for the record ids, e.g. opp={recid} for ConnectWise or opp={$ticket_id}&quote={$po_ref} for Halo. The PSA fills the tags in when the button is clicked, so they have to be spelled exactly as the PSA documents them.',
    quoteLinkPreview: 'Generated link',
    quoteLinkPreviewFolioId: '<folio id>',
    quoteLinkPreviewCapability: '<capability>',
    quoteLinkDescription: 'Description',
    quoteLinkDescriptionHelp: 'One line shown above the generated link.',
    quoteLinkInstructions: 'Setup steps',
    quoteLinkInstructionsHelp: 'Shown to the operator in order, verbatim: where in the PSA to add the button and what to paste where. Mention the substitution tags by name if the PSA makes the operator type them.',
    quoteLinkStep: (n: number) => `Step ${n}`,
    quoteLinkAddStep: 'Add step',
    quoteLinkRemoveStep: 'Remove step',
    quoteLinkMoveUp: 'Move up',
    quoteLinkMoveDown: 'Move down',
    quoteLinkAutoInstall: 'Automatic install (optional)',
    quoteLinkAutoInstallHelp: 'For PSAs whose API can create the button itself: the extension endpoint that says whether the connected account may add it, and the one that adds it. Leave both empty for PSAs where the operator adds the button by hand.',
    quoteLinkPermissionUrl: 'Permission URL',
    quoteLinkCreateUrl: 'Create URL',

    // Help links.
    addHelpLink: 'Add link',
    removeHelpLink: 'Remove link',
    helpLinkText: 'Link text',
    helpLinkUrl: 'URL',
};
